/**
 * 🗳️ VoteService — Service UNIFIÉ multi-produits pour TOUS les votes / réponses de polls
 *
 * ⚠️ POINT D'ENTRÉE UNIQUE pour insérer/modifier une réponse à un sondage,
 *    quel que soit le type de poll (date / form / quizz / availability).
 *
 * 🚫 N'écris JAMAIS un nouveau service par produit (FormResponseService,
 *    QuizzVoteService, etc.) — étends VoteService avec un nouveau `responseType`.
 *    Cf [Docs/ARCHITECTURE/2026-02-11-PLAN-MIGRATION-VOTES-SUPABASE.md]
 *    et [Docs/ARCHITECTURE/2026-04-01-STORAGE-ARCHITECTURE-FINAL.md].
 *
 * 🚫 N'écris JAMAIS `supabase.from("poll_responses").insert(...)` directement.
 *    La RLS UPDATE/DELETE anon a été lockée à `service_role` le 23/04/2026
 *    (cf migration 20260423_upsert_poll_response_rpc.sql, étape 1c bug-hunt).
 *    Toutes les mutations passent par les RPC SECURITY DEFINER :
 *      - INSERT/UPDATE  → `upsert_poll_response`
 *      - SELECT         → `get_poll_responses`
 *
 * 📋 ARCHITECTURE
 *
 * Mapping `Vote` (TS) ↔ table `poll_responses` (DB) :
 *   - pollId             → poll_id              (uuid)
 *   - respondentName     → respondent_name      (text)  — clé d'unicité avec poll_id+response_type
 *   - responseType       → response_type        (text)  — 'date'|'form'|'quizz'|'availability'
 *   - stableFingerprint  → stable_fingerprint   (text)  — guest identity
 *   - responseData       → response_data        (jsonb) — payload spécifique au type :
 *                                                          • date  : { [optionId]: 'yes'|'no'|'maybe', voterName }
 *                                                          • form  : { responses: FormResponseItem[], email? }
 *                                                          • quizz : { answers, score, ... }
 *                                                          • availability : { selectedSlot, ... }
 *
 * UPSERT comportement :
 *   - ON CONFLICT (poll_id, respondent_name, response_type) DO UPDATE
 *     → un même utilisateur (= même `respondent_name`) qui re-vote sur le même
 *       sondage et même type met à jour sa réponse précédente (pas de doublon).
 *
 * Lecture : `get_poll_responses(uuid)` SECURITY DEFINER, exclut les PII
 *   (email/IP/user_agent) côté serveur.
 */

import { supabase } from "@core/lib/supabase";
import { logger } from "@core/lib/logger";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import { getDeviceId } from "@core/lib/guestIdentityService";

export type ResponseType = "date" | "form" | "quizz" | "availability";

export interface CastVoteInput {
  /** UUID du sondage cible. */
  pollId: string;
  /** Nom du votant — pour les guests, généralement le `stableFingerprint` tronqué. */
  respondentName: string;
  /** Type de poll auquel la réponse est rattachée. */
  responseType: ResponseType;
  /** Fingerprint guest (optionnel, recommandé). */
  stableFingerprint?: string;
  /**
   * Payload spécifique au type :
   *   - date  : `{ [optionId]: 'yes'|'no'|'maybe', voterName }`
   *   - form  : `{ responses: FormResponseItem[], email? }`
   *   - quizz : `{ answers, score, ... }`
   *   - availability : `{ selectedSlot, ... }`
   */
  responseData: Record<string, unknown>;
  /**
   * Si une réponse existe déjà sous ce `respondentName` mais avec une autre
   * empreinte (device), l'upsert lève `RESPONSE_NAME_TAKEN` au lieu d'écraser.
   * Le frontend propose alors « modifier la réponse existante » ; sur
   * confirmation, il rappelle `castVote` avec `allowNameTakeover: true` pour
   * réclamer la ligne existante (nouvelle empreinte + nouveau payload).
   */
  allowNameTakeover?: boolean;
}

/** Code d'erreur levé par `castVote` quand le nom est déjà pris (cf RPC). */
export const RESPONSE_NAME_TAKEN = "RESPONSE_NAME_TAKEN";

/**
 * Erreur typée : une réponse existe déjà sous ce nom (autre empreinte).
 * Les UI qui savent gérer le cas (form-polls) catchent `instanceof
 * ResponseNameTakenError` et proposent de modifier la réponse ; le message
 * sert de repli lisible pour celles qui ne le gèrent pas encore.
 */
export class ResponseNameTakenError extends Error {
  readonly code = RESPONSE_NAME_TAKEN;
  constructor(message = "Une réponse existe déjà sous ce nom sur ce sondage.") {
    super(message);
    this.name = "ResponseNameTakenError";
  }
}

export interface VoteRow {
  id: string;
  pollId: string;
  respondentName: string;
  responseType: ResponseType;
  responseData: Record<string, unknown>;
  stableFingerprint?: string;
  createdAt: string;
  updatedAt?: string;
}

export class VoteService {
  /**
   * Insère ou met à jour une réponse de sondage via le RPC SECURITY DEFINER
   * `upsert_poll_response`. ON CONFLICT (poll_id, respondent_name, response_type)
   * écrase la réponse précédente du même votant.
   *
   * @throws si `poll.status !== 'active'` (RPC raise `poll_not_active`)
   * @throws si paramètres requis manquants (RPC raise `missing_required_parameters`)
   */
  static async castVote(input: CastVoteInput): Promise<VoteRow> {
    if (!input.pollId) {
      throw ErrorFactory.validation(
        "pollId is required",
        "pollId est requis pour enregistrer un vote",
      );
    }
    if (!input.respondentName) {
      throw ErrorFactory.validation(
        "respondentName is required",
        "Nom du votant requis",
      );
    }
    if (!input.responseType) {
      throw ErrorFactory.validation(
        "responseType is required",
        "Type de réponse requis",
      );
    }

    const rpcPromise = supabase.rpc("upsert_poll_response", {
      p_poll_id: input.pollId,
      p_respondent_name: input.respondentName,
      p_response_type: input.responseType,
      p_stable_fingerprint: input.stableFingerprint ?? null,
      p_response_data: input.responseData,
      p_allow_name_takeover: input.allowNameTakeover ?? false,
    });

    // 90s timeout pour éviter les hangs PostgreSQL sur ON CONFLICT
    // (augmenté de 30s pour accommoder les contraintes ON CONFLICT complexes)
    const timeoutPromise = new Promise<{ data: null; error: Error }>(
      (_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(
                "RPC timeout: upsert_poll_response took too long (>90s). Server may be experiencing issues or your vote conflicts with an existing one.",
              ),
            ),
          90000,
        ),
    );

    let data, error;
    try {
      const result = await Promise.race([rpcPromise, timeoutPromise]);
      data = result.data;
      error = result.error;
    } catch (timeoutErr) {
      error = timeoutErr as Error;
    }

    if (error || !data) {
      // Nom déjà pris (autre empreinte) : erreur typée → le frontend propose
      // de modifier la réponse existante (retry avec allowNameTakeover).
      if (
        error?.message?.includes(RESPONSE_NAME_TAKEN) ||
        (error as { details?: string })?.details?.includes(RESPONSE_NAME_TAKEN)
      ) {
        throw new ResponseNameTakenError();
      }
      if (error?.message?.includes("forbidden_overwrite")) {
        throw ErrorFactory.validation(
          "forbidden_overwrite",
          "Ce nom est déjà utilisé par un autre votant sur ce sondage. Choisis un autre nom.",
        );
      }
      logError(error ?? new Error("upsert_poll_response returned null"), {
        component: "VoteService",
        operation: "castVote",
        pollId: input.pollId,
      });
      throw error ?? new Error("VoteService.castVote returned null");
    }

    logger.info("Vote upserted", "vote", {
      pollId: input.pollId,
      responseType: input.responseType,
      voteId: data.id,
    });

    return mapRowToVote(data);
  }

  /**
   * Liste toutes les réponses d'un sondage via RPC SECURITY DEFINER
   * `get_poll_responses` (PII exclues côté serveur).
   *
   * Pour filtrer par type de poll (ex: ne récupérer que les réponses form),
   * utiliser `listByPollIdAndType()`.
   */
  static async listByPollId(pollId: string): Promise<VoteRow[]> {
    if (!pollId) return [];

    const { data, error } = await supabase.rpc("get_poll_responses", {
      p_poll_id: pollId,
      p_device_id: getDeviceId(),
    });

    if (error) {
      logError(error, {
        component: "VoteService",
        operation: "listByPollId",
        pollId,
      });
      throw error;
    }

    return ((data as any[]) ?? []).map(mapRowToVote);
  }

  /**
   * Liste les réponses d'un type spécifique pour un sondage.
   * Utile quand un poll multi-types stocke plusieurs `response_type` (rare).
   */
  static async listByPollIdAndType(
    pollId: string,
    responseType: ResponseType,
  ): Promise<VoteRow[]> {
    const all = await VoteService.listByPollId(pollId);
    return all.filter((row) => row.responseType === responseType);
  }

  /**
   * Compte le nombre de réponses d'un sondage (filtre optionnel par type).
   */
  static async countByPollId(
    pollId: string,
    responseType?: ResponseType,
  ): Promise<number> {
    const all = await VoteService.listByPollId(pollId);
    if (responseType) {
      return all.filter((row) => row.responseType === responseType).length;
    }
    return all.length;
  }

  /**
   * Anonymise toutes les réponses d'un sondage via RPC SECURITY DEFINER
   * `anonymize_poll_responses_for_poll`. Gate ownership côté serveur :
   * seul le créateur du sondage (auth.uid() OU guest_fingerprint matchant)
   * peut appeler.
   *
   * Effets : `respondent_name → NULL` et strip des clés PII connues
   * (`email`, `respondentEmail`, `voterEmail`, `name`, `voterName`,
   * `respondentName`) dans `response_data`.
   *
   * @returns nombre de réponses effectivement anonymisées
   * @throws si l'appelant n'est pas le créateur (`not_poll_creator`)
   */
  static async anonymizeForPoll(
    pollId: string,
    guestFingerprint?: string,
  ): Promise<{ anonymizedCount: number }> {
    if (!pollId) {
      throw ErrorFactory.validation("pollId is required", "pollId est requis");
    }

    const { data, error } = await supabase.rpc(
      "anonymize_poll_responses_for_poll",
      {
        p_poll_id: pollId,
        p_guest_fingerprint: guestFingerprint ?? null,
      },
    );

    if (error) {
      logError(error, {
        component: "VoteService",
        operation: "anonymizeForPoll",
        pollId,
      });
      throw error;
    }

    const row = Array.isArray(data) ? data[0] : data;
    const count =
      typeof row?.anonymized_count === "number" ? row.anonymized_count : 0;

    logger.info("Responses anonymized", "vote", {
      pollId,
      anonymizedCount: count,
    });

    return { anonymizedCount: count };
  }
}

function mapRowToVote(row: any): VoteRow {
  return {
    id: row.id,
    pollId: row.poll_id,
    respondentName: row.respondent_name,
    responseType: row.response_type as ResponseType,
    responseData: (row.response_data ?? {}) as Record<string, unknown>,
    stableFingerprint: row.stable_fingerprint ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at ?? undefined,
  };
}
